import { useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { PieChart as PieIcon, BarChart3 } from "lucide-react";
import { DATABASE } from "../../data/whitespaces";
import { Reveal, MaskLine } from "../../lib/motionPrimitives";
import EmblemWatermark from "../EmblemWatermark";

const SHADES = ["#00f0ff", "#f5a524", "#2b6f78", "#12a3b0", "#a86f18"];
const tooltipStyle = {
  background: "#0a0a0a",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  fontSize: 12,
  color: "#fff",
};
const tooltipItemStyle = { color: "#fff" };
const tooltipLabelStyle = { color: "#fff" };

const ATTR_KEYS = ["gender", "age", "salary", "credit", "profession", "geography"];

export const Database = () => {
  const [attrKey, setAttrKey] = useState("gender");
  const [chart, setChart] = useState("donut");
  const attr = DATABASE.attributes[attrKey];
  const suffix = attr.unit;

  return (
    <section id="database" data-testid="database-section" className="relative px-6 py-14 md:px-12 md:py-20">
      <EmblemWatermark className="-left-20 bottom-0 h-[38vw] w-[38vw] max-w-[560px] -rotate-6" />
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-8 flex items-center gap-4">
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-teal">04 — Data Reservoir</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/30">Whitespaces</span>
        </div>

        <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-end">
          <div>
            <h2 className="font-display text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-6xl lg:text-7xl">
              <MaskLine className="pb-[0.08em]">The reservoir.</MaskLine>
            </h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8"
            >
              <div
                className="font-display text-6xl font-bold tracking-tighter text-teal md:text-8xl"
                style={{ textShadow: "0 0 40px rgba(0,240,255,0.3)" }}
              >
                {DATABASE.total}
              </div>
              <div className="mt-2 font-body text-[12px] uppercase tracking-[0.25em] text-white/45">
                {DATABASE.totalLabel} — deterministic & consented
              </div>
            </motion.div>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-md font-body text-base leading-relaxed text-white/55">
              A living reservoir of first-party leads enriched with declared, behavioral and purchase
              signals. Slice it by any dimension below — every segment is activation-ready.
            </p>
          </Reveal>
        </div>

        {/* Explorer */}
        <div className="mt-16 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl">
          {/* Controls */}
          <div className="flex flex-col gap-4 border-b border-white/10 p-5 md:flex-row md:items-center md:justify-between md:p-6">
            <div className="flex flex-wrap gap-2" data-testid="db-filters">
              {ATTR_KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => setAttrKey(k)}
                  data-testid={`db-filter-${k}`}
                  className={`rounded-full border px-4 py-2 font-body text-[12px] uppercase tracking-[0.1em] transition-colors duration-300 ${
                    attrKey === k
                      ? "border-teal bg-teal text-black"
                      : "border-white/15 text-white/55 hover:border-white/40"
                  }`}
                >
                  {DATABASE.attributes[k].label}
                </button>
              ))}
            </div>
            <div className="flex gap-2" data-testid="db-charttype">
              <ChartToggle active={chart === "donut"} onClick={() => setChart("donut")} testId="db-chart-donut">
                <PieIcon size={16} /> Donut
              </ChartToggle>
              <ChartToggle active={chart === "bar"} onClick={() => setChart("bar")} testId="db-chart-bar">
                <BarChart3 size={16} /> Bars
              </ChartToggle>
            </div>
          </div>

          {/* Chart + legend */}
          <div className="grid gap-8 p-6 md:grid-cols-[1.3fr_1fr] md:p-10">
            <>
              <motion.div
                key={attrKey + chart}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="h-[320px] w-full"
                data-testid={`db-chart-${attrKey}`}
              >
                {chart === "donut" ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={attr.data}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={80}
                        outerRadius={130}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {attr.data.map((_, i) => (
                          <Cell key={i} fill={SHADES[i % SHADES.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={tooltipStyle}
                        itemStyle={tooltipItemStyle}
                        labelStyle={tooltipLabelStyle}
                        formatter={(v) => `${v}${suffix}`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={attr.data} layout="vertical" margin={{ left: 10, right: 30 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 13 }}
                        width={120}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.03)" }}
                        contentStyle={tooltipStyle}
                        itemStyle={tooltipItemStyle}
                        labelStyle={tooltipLabelStyle}
                        formatter={(v) => `${v}${suffix}`}
                      />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
                        {attr.data.map((_, i) => (
                          <Cell key={i} fill={SHADES[i % SHADES.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </motion.div>

              <motion.div
                key={attrKey + "legend"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col justify-center gap-4"
              >
                <div className="font-body text-[11px] uppercase tracking-[0.2em] text-teal">
                  {attr.label} distribution
                </div>
                {attr.data.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-4">
                    <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: SHADES[i % SHADES.length] }} />
                    <span className="flex-1 font-body text-sm text-white/70">{d.name}</span>
                    <span className="font-display text-lg font-medium text-white">
                      {d.value}
                      {suffix}
                    </span>
                  </div>
                ))}
                <p className="mt-4 border-t border-white/10 pt-4 font-body text-[13px] leading-relaxed text-white/45">
                  {attr.note}
                </p>
              </motion.div>
            </>
          </div>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 max-w-2xl font-body text-sm leading-relaxed text-white/40">
            Every dimension cross-references with the rest — income against geography, credit against
            profession — so each of the six personas is activated on real, deterministic signal.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const ChartToggle = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 rounded-full border px-4 py-2 font-body text-[12px] uppercase tracking-[0.1em] transition-colors duration-300 ${
      active ? "border-teal text-teal" : "border-white/15 text-white/50 hover:border-white/40"
    }`}
  >
    {children}
  </button>
);

export default Database;
